import { Avatar, PopupMenu, PopupMenuButton, PopupMenuContainer, PopupMenuItem } from '@components'
import { LanguagePicker } from '@containers/LanguagePicker'
import { useDebugMode, useFeaturebaseWishlistUrl, useTheme } from '@hooks'
import { useCurrentUser, useIsNocfoStaff, useLogOut } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { getUserSettingsUrl } from '@constants/urls'
import {
  FaBug,
  FaComments,
  FaGift,
  FaHome,
  FaLightbulb,
  FaPenNib,
  FaQuestionCircle,
  FaSignOutAlt,
  FaUserCog
} from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { AppMarketPicker } from './AppMarketPicker'
import { AvatarButton } from './Header.styled'
import { getUserDisplayName, isFeaturebaseEnabled, showFeaturebase } from '@root/utils'
import { allowsAppMarketOverride } from '@root/utils/marketHost'

export const AvatarMenu: React.FC = () => {
  const [t] = useTranslation()
  const theme = useTheme()
  const logOut = useLogOut()
  const [debugMode, setDebugMode] = useDebugMode()

  const { data: user } = useCurrentUser()
  const displayName = getUserDisplayName(user)
  const isNocfoStaff = useIsNocfoStaff()
  const featurebaseUrl = t('link.featurebase')
  const featurebaseWishlistUrl = useFeaturebaseWishlistUrl(featurebaseUrl)

  return (
    <PopupMenuContainer>
      <PopupMenu>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <PopupMenuItem icon={<FaHome />} title={t('userMenu.linkToFrontpage')} />
        </Link>
        <a
          href={t('link.docs.main')}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <PopupMenuItem icon={<FaQuestionCircle />} title={t('link.docsTitle')} />
        </a>
        {isFeaturebaseEnabled() && (
          <PopupMenuItem
            icon={<FaComments />}
            title={t('userMenu.getHelp')}
            onClick={() => showFeaturebase('messages')}
          />
        )}
        <a
          href={t('link.feedback')}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <PopupMenuItem icon={<FaPenNib />} title={t('link.feedbackTitle')} />
        </a>

        <a
          href={featurebaseWishlistUrl}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <PopupMenuItem icon={<FaLightbulb />} title={t('userMenu.wishList')} />
        </a>

        <a
          href={t('link.affiliate')}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <PopupMenuItem icon={<FaGift />} title={t('userMenu.affiliate')} />
        </a>

        {isNocfoStaff && (
          <PopupMenuItem
            icon={<FaBug />}
            onClick={() => setDebugMode(!debugMode)}
            title={t('userMenu.debug')}
          />
        )}

        <hr />
        {allowsAppMarketOverride() && <AppMarketPicker />}
        <LanguagePicker />

        <a
          href={getUserSettingsUrl()}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <PopupMenuItem icon={<FaUserCog />} title={t('link.userSettings')} />
        </a>

        <PopupMenuItem
          icon={<FaSignOutAlt />}
          onClick={() => logOut()}
          title={t('userMenu.logOut')}
        />
      </PopupMenu>

      <PopupMenuButton>
        <AvatarButton type="button" aria-label={displayName ?? t('link.userSettings')}>
          <Avatar
            src={user?.avatar_url}
            size={32}
            name={displayName}
            color={theme.colors.primary}
          />
        </AvatarButton>
      </PopupMenuButton>
    </PopupMenuContainer>
  )
}
